import React, { useState, useMemo } from 'react';
import { Form, Row, Col } from 'reactstrap';
import { ModalButtonBuilder, RegisteredInputsBuilder, ToastBuilder, ButtonBuilder } from '../../../../../../../../../GlobalComponents/Builders';
import { buildSelectOptionsFromArray, handleCopy } from '../../../../../../../../../GlobalComponents/Helpers';

const SOURCE_ROLES = [
    { label: 'Ignore', value: 'ignore' },
    { label: 'Board', value: 'board' },
    { label: 'Type', value: 'type' },
    { label: 'SubType', value: 'subtype' },
    { label: 'Item', value: 'item' },
    { label: 'Dest Chain', value: 'destination_chain' },
];

/**
 * ImportAssociationMappingsModal — Smart importer for composite association mappings.
 *
 * Paste tabular data, define column roles (Board, Type, SubType, Item + Dest Chain levels),
 * then the backend resolves source associations and destination chain values.
 */
const ImportAssociationMappingsModal = ({
    sourceResourceId,
    sourceResourceName,
    handleDMTAction,
    getResourceValueMappings,
    actionRegister,
    actionErrors,
    actionControl,
    actionSetValue,
    actionReset,
    onImportResult,
    lastImportSettings,
    setLastImportSettings,
}) => {
    const [step, setStep] = useState(1);
    const [parsedHeaders, setParsedHeaders] = useState([]);
    const [parsedRows, setParsedRows] = useState([]);
    const [columnDefs, setColumnDefs] = useState([]);
    const [headersIncluded, setHeadersIncluded] = useState(false);
    const [allowOverwrite, setAllowOverwrite] = useState(false);
    const [includeDescendants, setIncludeDescendants] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const [isImporting, setIsImporting] = useState(false);
    const [lastColumnDefs, setLastColumnDefs] = useState(null);
    const [lastHeadersIncluded, setLastHeadersIncluded] = useState(false);

    const resetState = () => {
        setStep(lastColumnDefs ? 1 : 1);
        setParsedRows([]);
        setImportResult(null);
        actionSetValue('assocImportData' + sourceResourceId, '');
        // Keep lastColumnDefs, headersIncluded, allowOverwrite, includeDescendants
    };

    const handleParseData = () => {
        const raw = actionControl._formValues?.['assocImportData' + sourceResourceId] || '';
        if (!raw.trim()) return ToastBuilder('error', 'No data to parse');

        const lines = raw.trim().split('\n');
        if (lines.length < 1) return ToastBuilder('error', 'Need at least 1 row');

        const firstRow = lines[0].split('\t');
        const dataRows = lines.slice(headersIncluded ? 1 : 0).map(l => l.split('\t'));

        const headers = headersIncluded
            ? firstRow.map(h => h.trim())
            : firstRow.map((_, i) => `Column ${i + 1}`);

        // Reuse last column defs if column count matches
        if (lastImportSettings && lastImportSettings.columnDefs && lastImportSettings.columnDefs.length === headers.length) {
            setParsedHeaders(headers);
            setParsedRows(dataRows);
            setColumnDefs(lastImportSettings.columnDefs);
            setAllowOverwrite(lastImportSettings.allowOverwrite || false);
            setIncludeDescendants(lastImportSettings.includeDescendants || false);
            setStep(2);
            return;
        }

        // Auto-detect column roles
        const autoDefs = headers.map((h) => {
            const lower = h.toLowerCase();
            if (lower.includes('board')) return { role: 'board', level: null };
            if (lower.includes('type') && !lower.includes('sub')) return { role: 'type', level: null };
            if (lower.includes('subtype') || lower.includes('sub type')) return { role: 'subtype', level: null };
            if (lower.includes('item') && !lower.includes('fs')) return { role: 'item', level: null };
            if (lower.includes('category') || lower.includes('cat')) return { role: 'destination_chain', level: null };
            return { role: 'ignore', level: null };
        });

        // Assign chain levels to destination columns
        let chainLevel = 0;
        for (let i = 0; i < autoDefs.length; i++) {
            if (autoDefs[i].role === 'destination_chain') {
                autoDefs[i].level = chainLevel++;
            }
        }

        setParsedHeaders(headers);
        setParsedRows(dataRows);
        setColumnDefs(autoDefs);
        setStep(2);
    };

    const handleColumnRoleChange = (idx, newRole) => {
        const updated = [...columnDefs];
        if (newRole === 'destination_chain') {
            const existingLevels = updated.filter(d => d.role === 'destination_chain').map(d => d.level);
            const nextLevel = existingLevels.length > 0 ? Math.max(...existingLevels) + 1 : 0;
            updated[idx] = { role: newRole, level: nextLevel };
        } else {
            updated[idx] = { role: newRole, level: null };
        }
        setColumnDefs(updated);
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const hasSource = columnDefs.some(d => ['board', 'type', 'subtype', 'item'].includes(d.role));
        const hasChain = columnDefs.some(d => d.role === 'destination_chain');

        if (!hasSource) return ToastBuilder('error', 'Must define at least one source column (Board, Type, SubType, or Item)');
        if (!hasChain) return ToastBuilder('error', 'Must define at least one Destination Chain column');

        const columnsConfig = columnDefs.map(d => {
            if (d.role === 'destination_chain') return { role: d.role, level: d.level };
            return { role: d.role };
        });

        const rawData = parsedRows.map(row => row.join('\t')).join('\n');

        setIsImporting(true);
        ToastBuilder('success', 'Importing mappings...', {}, 3000);

        handleDMTAction('load-association-mappings', sourceResourceId, (results) => {
            setIsImporting(false);
            const data = results?.data || {};
            if (results?.success || data?.success) {
                const msg = data.message || `${data.updated || 0} mapping(s) processed`;
                const skipped = data.skipped || [];
                const updated = data.updated || 0;
                // Persist settings for next import
                if (setLastImportSettings) setLastImportSettings({ columnDefs, allowOverwrite, includeDescendants });
                setLastColumnDefs(columnDefs);
                setLastHeadersIncluded(headersIncluded);
                if (onImportResult) onImportResult({ updated, skipped, message: msg, rawRows: parsedRows });
                setImportResult({ updated, skipped, message: msg, rawRows: parsedRows });
                setStep(3);
                if (skipped.length > 0) {
                    ToastBuilder('warning', msg, {}, 8000);
                } else {
                    ToastBuilder('success', msg, {}, 8000);
                }
                getResourceValueMappings();
            } else {
                const errMsg = data?.message || 'Failed to load mappings';
                setImportResult({ updated: 0, skipped: [], message: errMsg, error: true });
                setStep(3);
                ToastBuilder('error', errMsg, {}, 8000);
            }
        }, {
            data: rawData,
            columns: columnsConfig,
            allowOverwrite,
            includeDescendants,
            headersIncluded: false,
        });
    };

    const sampleRows = parsedRows.slice(0, 3);

    return (
        <ModalButtonBuilder title='Import Associations' className='btn-outline-danger'
            formId={'load-assoc-mappings-form-' + sourceResourceId}
            modalTitle={`Import ${sourceResourceName} Associations`}
            style={{ minWidth: '700px', maxWidth: '800px' }}
            onClick={() => { setStep(1); setParsedRows([]); setImportResult(null); actionSetValue('assocImportData' + sourceResourceId, ''); }}
            onSubmit={false}
            onCancelLabel={step === 3 ? 'Close' : 'Cancel'}
            body={
                <Form id={'load-assoc-mappings-form-' + sourceResourceId} onSubmit={handleSubmit}>
                    {step === 1 && (
                        <>
                            <p>Paste tab-separated data from your spreadsheet.</p>
                            <ul style={{ fontSize: '12px', color: '#636E72' }}>
                                <li>Include column headers in the first row for auto-detection</li>
                                <li>Source columns: <b>Board</b>, <b>Type</b>, <b>SubType</b>, <b>Item</b></li>
                                <li>Destination columns: <b>FS Category</b>, <b>FS Subcategory</b>, <b>FS Item</b></li>
                                <li>Empty source columns act as "any" filter; empty destination columns stop at that level</li>
                            </ul>
                            <br />
                            <div className="mb-3">
                                <label className="form-check-label me-3">
                                    <input type="checkbox" className="form-check-input me-1"
                                        checked={headersIncluded}
                                        onChange={(e) => setHeadersIncluded(e.target.checked)}
                                    />
                                    First row contains headers
                                </label>
                            </div>
                            <textarea
                                className="form-control"
                                style={{ height: '200px', fontFamily: 'monospace', fontSize: '12px' }}
                                placeholder={'Board\tSubType\tItem\tFS Category\tFS Subcategory\tFS Item\nUnified - MS Problem Request\tEMR\t\tClinical Applications\t\t\nUnified - MS Help Desk\tSecurity\t1 - MFA Tools\tSecurity\tMFA\t'}
                                {...actionRegister('assocImportData' + sourceResourceId)}
                            />
                            <ButtonBuilder
                                label="Parse & Configure Columns"
                                className="btn-primary mt-3"
                                type="button"
                                useSpinner={false}
                                onClick={handleParseData}
                            />
                        </>
                    )}

                    {step === 2 && (
                        <>
                            <p style={{ fontSize: '13px' }}>
                                Define what each column represents. <strong>{parsedRows.length}</strong> data row(s) detected.
                            </p>

                            <div className="table-responsive mb-3" style={{ maxHeight: '150px', overflow: 'auto' }}>
                                <table className="table table-sm table-bordered" style={{ fontSize: '11px' }}>
                                    <thead>
                                        <tr>
                                            {parsedHeaders.map((h, i) => (
                                                <th key={i} style={{ minWidth: '100px', background: '#f8f9fa' }}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sampleRows.map((row, ri) => (
                                            <tr key={ri}>
                                                {row.map((cell, ci) => (
                                                    <td key={ci} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '150px' }}>{cell}</td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <h6>Column Roles</h6>
                            <div style={{ fontSize: '12px' }}>
                                {parsedHeaders.map((header, idx) => (
                                    <Row key={idx} className="mb-1 align-items-center">
                                        <Col sm={3} className="text-truncate" title={header}>
                                            <strong>{header}</strong>
                                        </Col>
                                        <Col sm={4}>
                                            <select
                                                className="form-select form-select-sm"
                                                value={columnDefs[idx]?.role || 'ignore'}
                                                onChange={(e) => handleColumnRoleChange(idx, e.target.value)}
                                            >
                                                {SOURCE_ROLES.map(r => (
                                                    <option key={r.value} value={r.value}>{r.label}</option>
                                                ))}
                                            </select>
                                        </Col>
                                        <Col sm={3}>
                                            {columnDefs[idx]?.role === 'destination_chain' && (
                                                <select
                                                    className="form-select form-select-sm"
                                                    value={columnDefs[idx]?.level ?? 0}
                                                    onChange={(e) => {
                                                        const updated = [...columnDefs];
                                                        updated[idx] = { ...updated[idx], level: parseInt(e.target.value) };
                                                        setColumnDefs(updated);
                                                    }}
                                                >
                                                    <option value={0}>Level 1 (Category)</option>
                                                    <option value={1}>Level 2 (Subcategory)</option>
                                                    <option value={2}>Level 3 (Item)</option>
                                                </select>
                                            )}
                                        </Col>
                                    </Row>
                                ))}
                            </div>

                            <hr />
                            <h6>Options</h6>
                            <div className="mb-2">
                                <label className="form-check-label me-4">
                                    <input type="checkbox" className="form-check-input me-1"
                                        checked={includeDescendants}
                                        onChange={(e) => setIncludeDescendants(e.target.checked)}
                                    />
                                    Include non-terminal descendants
                                </label>
                                <small className="text-muted d-block ms-4">When checked, also updates child mappings under the matched source level</small>
                            </div>
                            <div className="mb-2">
                                <label className="form-check-label me-4">
                                    <input type="checkbox" className="form-check-input me-1"
                                        checked={allowOverwrite}
                                        onChange={(e) => setAllowOverwrite(e.target.checked)}
                                    />
                                    Allow overwriting existing mappings
                                </label>
                                <small className="text-muted d-block ms-4">When checked, overwrites already-mapped and approved rows</small>
                            </div>
                            <hr />
                            <div className='d-flex justify-content-between'>
                                <ButtonBuilder
                                    label="Back"
                                    className="btn-outline-secondary"
                                    type="button"
                                    useSpinner={false}
                                    onClick={() => setStep(1)}
                                />
                                <ButtonBuilder
                                    label={isImporting ? "Importing..." : "Import Mappings"}
                                    className="btn-primary"
                                    type="submit"
                                    useSpinner={isImporting}
                                    disabled={isImporting}
                                />
                            </div>
                        </>
                    )}

                    {step === 3 && importResult && (
                        <>
                            {importResult.error ? (
                                <h6 className='txt-danger'>✗ {importResult.message}</h6>
                            ) : (
                                <h6 className='text-success'>✓ {importResult.message}</h6>
                            )}
                            {importResult.skipped && importResult.skipped.length > 0 && (
                                <>
                                    <div className='d-flex justify-content-between align-items-center mt-3'>
                                        <h6 className='text-warning mb-0'>⚠ {importResult.skipped.length} skipped:</h6>
                                        <ButtonBuilder
                                            label='Copy Failed Rows'
                                            className='btn-outline-secondary py-1 px-2'
                                            style={{ fontSize: '11px' }}
                                            useSpinner={false}
                                            onClick={() => {
                                                const rawRows = importResult.rawRows || [];
                                                const failedLines = importResult.skipped.map(s => {
                                                    const rowIdx = (s.row || 1) - 1;
                                                    return rawRows[rowIdx] ? rawRows[rowIdx].join('\t') : `Row ${s.row}`;
                                                });
                                                handleCopy(failedLines.join('\n'));
                                                ToastBuilder('success', 'Failed rows copied to clipboard');
                                            }}
                                        />
                                    </div>
                                    <div style={{ maxHeight: '300px', overflowY: 'auto' }} className='mt-2'>
                                        <table className='table table-sm' style={{ fontSize: '11px' }}>
                                            <thead><tr><th>Row</th><th>Reason</th></tr></thead>
                                            <tbody>
                                                {importResult.skipped.map((s, i) => (
                                                    <tr key={i}>
                                                        <td>{s.row}</td>
                                                        <td>{s.reason}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}
                            <ButtonBuilder
                                label="Done"
                                className="btn-primary mt-3"
                                type="button"
                                useSpinner={false}
                                onClick={resetState}
                            />
                        </>
                    )}
                </Form>
            }
        />
    );
};

export default ImportAssociationMappingsModal;
